import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Store, Action } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { FormGroup, Validators, FormArray } from '@angular/forms';

import { ModuleConfiguration } from '../dynamicDataSet.module.config';
import { DynamicDataSetModel } from '../models/DynamicDataSetModel';
import * as fromStore from '../store/reducers';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { PhysicalAddressModel } from '../../common/models/PhysicalAddressModel';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { AbstractControl } from '@angular/forms/src/model';
import { Subject } from 'rxjs/Subject';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { MatDialog } from '@angular/material';
import { DeleteDynamicDataSetConfirmationDialogComponent } from './delete-dynamic-dataset-confirmation-dialog.component';
import { DynamicEntityModel } from '../models/DynamicEntityModel';
import { PropertyModel } from '../models/PropertyModel';
import { DataLinkModel } from '../models/DataLinkModel';
import { FinalizeDynamicDataSetAction } from '../store/actions/FinalizeDynamicDataSetAction';
import { LookupModel } from '../../common/models/LookupModel';
import { DynamicRuleProfileModel } from '../models/DynamicRuleProfileModel';
import { DynamicRuleModel } from '../models/DynamicRuleModel';
import { CreateDraftDynamicDataSetAction } from '../store/actions/CreateDraftDynamicAction';
import { UnfinalizeDynamicDataSetAction } from '../store/actions/UnfinalizeDynamicDataSet';
import { ACTION_ERROR, ActionError } from '../../pillar/common/actions/ActionError';
import { ActionBase } from '../../core/store/action/ActionBase';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';
import { DataWarehouseService } from '../../datawarehouse/service/DataWarehouseService';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-dynamic-dataset-workspace',
  templateUrl: './dynamic-dataset-workspace.component.html',
  styleUrls: ['./dynamic-dataset-workspace.component.scss']
})
export class DynamicDataSetWorkspaceComponent implements OnInit, OnDestroy {
  dynamicDataSet: DynamicDataSetModel;
  key: string;
  loading$: Observable<boolean>;
  dataSets$: Observable<LookupModel[]>;
  reportingNotConfigured$: Observable<boolean>;
  errors$: Observable<string[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  properties$: BehaviorSubject<PropertyModel[]> = new BehaviorSubject<PropertyModel[]>([]);
  currentProperties: PropertyModel[] = [];
  dataLinks$: BehaviorSubject<DataLinkModel[]> = new BehaviorSubject<DataLinkModel[]>([]);
  entityForm: FormGroup;
  propertiesForm: FormGroup;
  dataLinksForm: FormGroup;
  rulesForm: FormGroup;
  formArrayInitMethods = new Map<string, () => void>();
  dataSetEntities: Observable<LookupModel[]>[] = [];
  dataSetEntityProperties: Observable<LookupModel[]>[] = [];
  readOnly: boolean;
  actionIdentifiers: string[] = [];
  jurisdictionalDomain: string = 'bird';

  editPermission: string = 'DynamicDataSetModule.DynamicDataSets:Edit';
  unfinalizePermission: string = 'DynamicDataSetModule.DynamicDataSets:Unfinalize';

  propertyTypes: LookupModel[] = [
    { key: 'string', description: 'String' },
    { key: 'int?', description: 'Int' },
    { key: 'decimal?', description: 'Decimal' },
    { key: 'bool?', description: 'Boolean' },
    { key: 'DateTime?', description: 'DateTime' },
    { key: 'Vocabulary', description: 'Vocabulary' }
  ];


  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private dataWarehouseService: DataWarehouseService,
    private appConfigurationService: AppConfigurationService) {
    this.jurisdictionalDomain = appConfigurationService.configuration.jurisdictionalDomain;
  }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }


  ngOnDestroy() {
    this.storeDispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, DynamicDataSetModel));
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromStore.getDynamicDataSetListLoading);
    this.errors$ = this.store.select(fromStore.getDynamicDataSetListErrors);
    this.dataSets$ = this.dataWarehouseService.getDataSets();
    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];

      this.storeDispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DynamicDataSetModel, { key: this.key }));
    });

    this.actions$.ofType(ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .filter((a: ActionError) => this.actionIdentifiers.includes(a.payload.action.identifier))
      .subscribe((a: ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
        this.store.dispatch(new dashboardActions.StopLoadingAction(ModuleConfiguration.moduleName, DynamicDataSetModel));
      });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicDataSetModel, 'name'),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicDataSetModel, DynamicEntityModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel)
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<DynamicDataSetModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelErrorAction<DynamicDataSetModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    var removePropertyType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel);
    this.actions$.ofType(removePropertyType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var propertyFormArray = (this.propertiesForm.controls['properties'] as FormArray);
        var index = propertyFormArray.controls.indexOf(payload.control);
        propertyFormArray.removeAt(index);
      });
    var removeDataLinkType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel);
    this.actions$.ofType(removeDataLinkType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var dataLinksArray = (this.dataLinksForm.controls['dataLinks'] as FormArray);
        var index = dataLinksArray.controls.indexOf(payload.control);
        dataLinksArray.removeAt(index);
        this.dataSetEntities.splice(index, 1);
      });
    var removeRuleType = workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel);
    this.actions$.ofType(removeRuleType)
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var array = (this.rulesForm.controls['businessRules'] as FormArray);
        var index = array.controls.indexOf(payload.control);
        array.removeAt(index);
      });
    this.store.select(fromStore.getDynamicDataSetSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: DynamicDataSetModel) => {
        if (v) {
          this.dynamicDataSet = v;
          if (this.key !== this.dynamicDataSet.key) {
            this.key = this.dynamicDataSet.key;
            this.router.navigateByUrl('dynamic-dataset-workspace/' + this.key);
          }
          if (!this.entityForm) {
            this.initForms();
          } else {
            this.updateControls((this.propertiesForm.get("properties") as FormArray),
              this.dynamicDataSet.dynamicEntity.properties.map(c => this.initProperty(c)));
            this.updateControls((this.dataLinksForm.get("dataLinks") as FormArray),
              this.dynamicDataSet.dynamicEntity.dataLinks.map(c => this.initDataLink(c)));
            this.updateControls((this.rulesForm.get("businessRules") as FormArray),
              this.dynamicDataSet.dynamicEntity.businessRules.map(c => this.initRule()));
            this.updateFormValues();
          }
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private updateControls(array: FormArray, controls: FormGroup[]) {
    while (array.controls.length > 0) {
      array.removeAt(0);
    }
    controls.forEach(c => array.push(c));
  }

  initForms(): void {
    this.entityForm = this.formBuilder.group({
      key: [''],
      isActive: [''],
      name: ['', Validators.compose([Validators.required, Validators.pattern('[A-Za-z]*'), Validators.maxLength(100)])],
      description: ['', Validators.compose([Validators.required, Validators.maxLength(500)])]
    },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicDataSetModel, DynamicEntityModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    this.propertiesForm = this.formBuilder.group({
      properties: this.formBuilder.array(this.dynamicDataSet.dynamicEntity.properties.map(c => this.initProperty(c)))
    });
    this.dataLinksForm = this.formBuilder.group({
      dataLinks: this.formBuilder.array(this.dynamicDataSet.dynamicEntity.dataLinks.map(c => this.initDataLink(c)))
    });
    this.rulesForm = this.formBuilder.group({
      businessRules: this.formBuilder.array(this.dynamicDataSet.dynamicEntity.businessRules.map(c => this.initRule()))
    });

    this.propertiesForm
      .get('properties')
      .valueChanges
      .takeUntil(this.destroyed$)
      .subscribe((v: PropertyModel[]) => {
        this.currentProperties = v;
        if (this.properties$) {
          this.updateDynamicDataLinkProperties();
        }
      });
    this.dataLinksForm
      .get('dataLinks')
      .valueChanges
      .takeUntil(this.destroyed$)
      .subscribe((v: DataLinkModel[]) => {
        if (this.dataLinks$) {
          this.dataLinks$.next(v.filter(d => d.key));
          this.updateDynamicDataLinkProperties();
        }
      });

    this.formArrayInitMethods.set('properties', () => this.initProperty());
    this.formArrayInitMethods.set('dataLinks', () => this.initDataLink());
    this.formArrayInitMethods.set('businessRules', () => this.initRule());

    setTimeout(() => {
        this.updateFormValues();
      },
      50);

  }

  updateDynamicDataLinkProperties() {
    var currentDataLinks = (this.dataLinksForm.get('dataLinks') as FormArray).getRawValue();
    this.properties$.next(this.currentProperties.filter(p => !currentDataLinks.find(d => d.propertyKey === p.key)));
  }

  updateFormValues(): void {

    if (this.dynamicDataSet.status === 'Draft') {
      this.entityForm.enable();
      this.propertiesForm.enable();
      this.dataLinksForm.enable();
      this.rulesForm.enable();
      this.readOnly = false;
      this.entityForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.entityForm.markAsPristine();
      this.propertiesForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.propertiesForm.markAsPristine();
      this.dataLinksForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.dataLinksForm.markAsPristine();
      this.rulesForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.rulesForm.markAsPristine();
      this.properties$.next(this.propertiesForm.get("properties").value);
    } else {
      this.entityForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.entityForm.markAsPristine();
      this.propertiesForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.propertiesForm.markAsPristine();
      this.dataLinksForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.dataLinksForm.markAsPristine();
      this.rulesForm.patchValue(this.dynamicDataSet.dynamicEntity);
      this.rulesForm.markAsPristine();
      this.properties$.next(this.propertiesForm.get("properties").value);

      setTimeout(() => {
          this.entityForm.disable();
          this.propertiesForm.disable();
          this.dataLinksForm.disable();
          this.rulesForm.disable();
        },
        300);
      this.readOnly = true;
    }

    this.updateDynamicDataLinkProperties();
  }

  initProperty(initialValue?: PropertyModel) {
    var formGroup = this.formBuilder.group({
      key: [''],
      isActive: [''],
      type: ['', Validators.required],
      valueSet: [''],
      name: ['', Validators.compose([Validators.required, Validators.pattern('[A-Za-z]*'), Validators.maxLength(100)])],
      isKeyField: [!initialValue && (this.propertiesForm.get('properties') as FormArray).controls.length === 0, Validators.required],
      columnLength: [''],
      columnLengthErrorMessage: ['', Validators.maxLength(500)],
      description: ['', Validators.compose([Validators.required, Validators.maxLength(500)])],
    },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    var typeControl = formGroup.get('type');
    typeControl
      .valueChanges
      .subscribe(
      (type: string) => {
        if (type === 'Vocabulary') {
          formGroup.get('valueSet').setValidators([Validators.required]);
          formGroup.get('valueSet').enable();
          formGroup.get('valueSet').markAsTouched();
        } else {
          formGroup.get('valueSet').setValidators([]);
          formGroup.get('valueSet').disable();
        }
        formGroup.get('valueSet').updateValueAndValidity();

        if (type === 'string' || type === 'Vocabulary') {
          formGroup.get('columnLength').setValidators([Validators.required, Validators.min(1)]);
          formGroup.get('columnLength').enable();
          formGroup.get('columnLengthErrorMessage').enable();
          formGroup.get('columnLength').markAsTouched();
          formGroup.get('columnLengthErrorMessage').markAsTouched();
        } else {
          formGroup.get('columnLength').setValidators([]);
          formGroup.get('columnLength').disable();
          formGroup.get('columnLengthErrorMessage').disable();
        }
        formGroup.get('columnLength').updateValueAndValidity();
        formGroup.get('columnLengthErrorMessage').updateValueAndValidity();
      }
    );
    return formGroup;
  }

  initDataLink(initialValue?: DataLinkModel) {
    var dataLinkControl = this.formBuilder.group({
      key: [''],
      isActive: [''],
      description: [''],
      propertyKey: ['', Validators.required],
      targetDataSet: ['', Validators.required],
      targetEntity: ['', Validators.required],
      targetField: ['', Validators.required],
      enforced: [true, Validators.required],
      parentDataLinkKey: ['']
    },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);

    if (this.dynamicDataSet.status === 'Draft' && (!initialValue || !initialValue.isActive)) {
      dataLinkControl
        .valueChanges
        .subscribe((v: DataLinkModel) => {
          var savedDataLink = this.dynamicDataSet.dynamicEntity.dataLinks.find(d => d.key === v.key);
          if (savedDataLink) {
            if (savedDataLink.targetDataSet !== v.targetDataSet) {
              savedDataLink.targetDataSet = v.targetDataSet;
              savedDataLink.targetEntity = undefined;
              savedDataLink.targetField = undefined;
              dataLinkControl.get('targetEntity').patchValue(savedDataLink.targetEntity);
              dataLinkControl.get('targetField').patchValue(savedDataLink.targetField);
            } else if (savedDataLink.targetEntity !== v.targetEntity) {
              savedDataLink.targetEntity = v.targetEntity;
              savedDataLink.targetField = undefined;
              dataLinkControl.get('targetField').patchValue(savedDataLink.targetField);
            }
          }
        });
    }
    this.dataSetEntities.push(dataLinkControl.get('targetDataSet')
      .valueChanges
      .switchMap((v: string) => {
        return this.dataWarehouseService.getDataSetEntities({ key: v, description: '' });
      }));
    this.dataSetEntityProperties.push(dataLinkControl.get('targetEntity')
      .valueChanges
      .switchMap((v: string) => {
        return this.dataWarehouseService.getDataSetEntityProperties({ key: v, description: '' });
      }));
    return dataLinkControl;
  }

  initRule() {
    var formGroup = this.formBuilder.group({
      key: [''],
      isActive: [''],
        name: ['', Validators.required],
        isEnabled: [false, Validators.required]
      },
      null,
      this.destroyed$,
      (control) => {
        this.storeDispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel, { key: this.key, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    return formGroup;
  }

  removeProperty(form, property: AutoSaveFormGroup, index) {
    if (!property.autoSaveContext.hasSavedValidValue) {
      var propertyFormArray = (form.controls['properties'] as FormArray);
      propertyFormArray.removeAt(index);
    } else {
      property.initializeRemove();
      this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, PropertyModel, { key: this.key, model: property.value, control: property }));
    }
  }

  addFormGroup(form, name) {
    form.controls[name].push(this.formArrayInitMethods.get(name)());
  }

  removeDataLink(form, dataLink: AutoSaveFormGroup, index) {
    if (!dataLink.autoSaveContext.hasSavedValidValue) {
      var array = (form.controls['dataLinks'] as FormArray);
      array.removeAt(index);
    } else {
      dataLink.initializeRemove();
      this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, DataLinkModel, { key: this.key, model: dataLink.value, control: dataLink }));
    }
  }

  removeRule(form, rule: AutoSaveFormGroup, index) {
    if (!rule.autoSaveContext.hasSavedValidValue) {
      var array = (form.controls['businessRules'] as FormArray);
      array.removeAt(index);
    } else {
      rule.initializeRemove();
      this.storeDispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel, { key: this.key, model: rule.value, control: rule }));
    }
  }

  getParentDataLinks(dataLinkKey) {
    return this.dynamicDataSet.dynamicEntity.dataLinks.filter(l => l.key !== dataLinkKey);
  }

  back() {
    this.router.navigateByUrl('/configuration');
  }
  delete() {
    this.dialog.open(DeleteDynamicDataSetConfirmationDialogComponent,
      {
        panelClass: 'customized-dialog',
        data:
          {
            name: this.dynamicDataSet.name,
            key: this.key
          }
      });
  }

  canFinalize() {
    return this.propertiesForm &&
      (this.propertiesForm.controls['properties'] as FormArray).value &&
      (this.propertiesForm.controls['properties'] as FormArray).value.filter(p => p.key).length > 0;
  }

  finalize() {
    this.storeDispatch(new FinalizeDynamicDataSetAction({ dynamicDataSetKey: this.dynamicDataSet.key }));
  }

  unfinalize() {
    this.storeDispatch(new UnfinalizeDynamicDataSetAction({ dynamicDataSetKey: this.dynamicDataSet.key }));
  }

  createDraft() {
    this.storeDispatch(new CreateDraftDynamicDataSetAction({ dynamicDataSetKey: this.dynamicDataSet.key }));
  }

  viewActive() {
    this.storeDispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DynamicDataSetModel, { key: this.dynamicDataSet.activeVersionKey }));
  }

  viewDraft() {
    this.storeDispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DynamicDataSetModel, { key: this.dynamicDataSet.draftVersionKey }));
  }

  goToRuleWorkspace(key: string) {
    this.router.navigateByUrl('/dynamic-rule-workspace/' + this.key + '/' + key);
  }

  getPropertyName(propertyKey: string) {
    return (this.propertiesForm.get("properties") as FormArray).getRawValue().find(p => p.key === propertyKey).name;
  }
}



// WEBPACK FOOTER //
// ./src/app/dynamicDataSets/components/dynamic-dataset-workspace.component.ts